import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getCabPackage, submitPackageEnquiry } from '../../api/cabsPublic';
import CabsBreadcrumbs from './CabsBreadcrumbs';

const CabsPackageEnquiry = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    customerName: '',
    phone: '',
    email: '',
    passengers: searchParams.get('passengers') || 1,
    tripStartDate: searchParams.get('tripStartDate') || '',
    budget: '',
    notes: '',
    consent: false,
    packageVariantId: searchParams.get('packageVariantId') || '',
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getCabPackage(slug);
        if (!cancelled) setPkg(res.data || res || null);
      } catch {
        if (!cancelled) setPkg(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!pkg?.variants?.length) return;
    const sorted = [...pkg.variants].sort((a, b) => Number(a.priceTotal || 0) - Number(b.priceTotal || 0));
    setForm((prev) => ({ ...prev, packageVariantId: prev.packageVariantId || sorted[0]?.id || '' }));
  }, [pkg]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await submitPackageEnquiry({
        packageSlug: slug,
        packageVariantId: form.packageVariantId || undefined,
        customerName: form.customerName,
        phone: form.phone,
        email: form.email || undefined,
        passengers: Number(form.passengers),
        tripStartDate: form.tripStartDate,
        budget: form.budget ? Number(form.budget) : undefined,
        notes: form.notes || undefined,
        consent: form.consent,
        sourcePage: 'package_detail',
      });
      const leadId = res?.data?.id || res?.id || '';
      navigate(`/cabs/enquiry/success?leadId=${encodeURIComponent(leadId)}&type=package`);
    } catch (err) {
      setError(err.message || 'Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const variantOptions = useMemo(() => {
    return (pkg?.variants || []).map((variant) => ({
      id: variant.id,
      label: `${variant.vehicleCategory?.name || 'Category'} - ${variant.seatConfig?.name || 'Seats'} (INR ${variant.priceTotal})`,
    }));
  }, [pkg]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Loading enquiry...
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Package not found.
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <CabsBreadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Cabs', to: '/cabs' },
          { label: 'Packages', to: '/cabs/packages' },
          { label: pkg.title, to: `/cabs/packages/${slug}` },
          { label: 'Enquiry' },
        ]}
      />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Package enquiry</h1>
            <p className="text-sm text-slate-600">Send your request for {pkg.title}.</p>
          </div>
          <Link
            to={`/cabs/packages/${slug}${location.search}`}
            className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600"
          >
            Back to details
          </Link>
        </div>

        {error && <div className="mt-4 rounded-xl bg-rose-50 p-3 text-xs text-rose-700">{error}</div>}

        <form className="mt-4 grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Name *</label>
            <input
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.customerName}
              onChange={(e) => handleChange('customerName', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Phone *</label>
            <input
              type="tel"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              inputMode="tel"
              pattern="[0-9+\-\s()]{7,20}"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email *</label>
            <input
              type="email"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Passengers *</label>
            <input
              type="number"
              min="1"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.passengers}
              onChange={(e) => handleChange('passengers', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Travel date *</label>
            <input
              type="date"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.tripStartDate}
              onChange={(e) => handleChange('tripStartDate', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Budget</label>
            <input
              type="number"
              min="0"
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.budget}
              onChange={(e) => handleChange('budget', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Variant</label>
            <select
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={form.packageVariantId}
              onChange={(e) => handleChange('packageVariantId', e.target.value)}
            >
              <option value="">Select a variant</option>
              {variantOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Notes</label>
            <textarea
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              rows="4"
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </div>
          <div className="md:col-span-2 flex items-start gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => handleChange('consent', e.target.checked)}
            />
            <span>I agree to be contacted about this enquiry.</span>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white"
            >
              {submitting ? 'Submitting...' : 'Submit enquiry'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default CabsPackageEnquiry;
